import { memo, useRef } from 'react';
import cn from 'classnames';
import { useAtomValue } from 'jotai';
import { Text } from '@pairy/pds-react';
import { useIsomorphicLayoutEffect } from '@pairy/react-hooks';

import useUpdateViewerPageState from '../../../../../hooks/useUpdateViewerPageState';
import { SetState } from '../../../../../types/helper';
import { ImageSourcesDto } from '../../../../../types/imageSources';
import { isFirstPageImageLoadedAtom } from '../../../../../store/index.atom';
import CloseButton from '../CloseButton';

import * as Styled from './index.styles';

export interface Props {
  isMobileDrawerOpen: boolean;
  setIsMobileDrawerOpen: SetState<boolean>;
  pages?: {
    thumnail: string;
    page: number;
    originalWidth: number;
    originalHeight: number;
    imageSources?: ImageSourcesDto;
  }[];
  translateKoToEn: (item: string) => string;
  blindPages?: number[] | null;
}

const MobilePageLandscapeNavigation = ({
  isMobileDrawerOpen,
  setIsMobileDrawerOpen,
  pages,
  translateKoToEn,
  blindPages,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { updateCurrentPage, currentPage } = useUpdateViewerPageState();

  useIsomorphicLayoutEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const currentPageElement = document.querySelector(
      `[data-current-page="true"]`,
    ) as HTMLLIElement;

    if (!(currentPageElement instanceof HTMLLIElement)) {
      return;
    }

    wrapperRef.current.scrollTo({
      left: currentPageElement.offsetLeft,
    });
  }, [wrapperRef.current, currentPage]);

  const isFirstPageImageLoaded = useAtomValue(isFirstPageImageLoadedAtom);

  if (!isFirstPageImageLoaded) {
    return null;
  }

  return (
    <>
      <Styled.Wrapper
        ref={wrapperRef}
        className={isMobileDrawerOpen ? 'visible' : 'hidden'}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        <Styled.InnerWrapper direction="column" gap={32}>
          <Styled.StackPageWrapper as="ul" gap={16} alignItems="center">
            {pages?.map(({ page, thumnail, imageSources }) => {
              const isCurrentPage = currentPage === page;

              return (
                <Styled.Page
                  key={page}
                  className={isCurrentPage ? 'current-page' : ''}
                  data-current-page={isCurrentPage}
                  onClick={() => {
                    /* NOTE: 첫 Blind Page는 Drawer로 이동이 가능하도록 */
                    if (
                      blindPages?.[0] !== page &&
                      blindPages?.includes(page)
                    ) {
                      return;
                    }

                    updateCurrentPage(page);

                    setIsMobileDrawerOpen(false);
                  }}
                >
                  <Styled.PageInnerWrapper
                    className={isCurrentPage ? 'current-page' : ''}
                  >
                    <Text
                      as="p"
                      variant="md-medium"
                      textAlign="center"
                      marginBottom={8}
                      color={isCurrentPage ? 'skyblue_500' : 'zinc_400'}
                    >
                      {page} {translateKoToEn('page')}
                    </Text>
                    {imageSources ? (
                      <Styled.BlindPictureImage
                        className={cn({
                          'is-blind': blindPages?.includes(page),
                          'current-page': isCurrentPage,
                        })}
                        alt={`${page}페이지 이미지`}
                        thumb={imageSources.thumb}
                        width={280}
                        height={160}
                        objectFit="contain"
                      />
                    ) : (
                      <Styled.BlindImage
                        className={cn({
                          'is-blind': blindPages?.includes(page),
                          'current-page': isCurrentPage,
                        })}
                        alt={`${page}페이지 이미지`}
                        src={thumnail}
                        width={280}
                        height={160}
                        quality={50}
                        objectFit="contain"
                        draggable={false}
                      />
                    )}
                  </Styled.PageInnerWrapper>
                </Styled.Page>
              );
            })}
          </Styled.StackPageWrapper>
          <CloseButton
            setIsMobileDrawerOpen={setIsMobileDrawerOpen}
            translateKoToEn={translateKoToEn}
          />
        </Styled.InnerWrapper>
      </Styled.Wrapper>
      {isMobileDrawerOpen && <Styled.Dimmer />}
    </>
  );
};

export default memo(MobilePageLandscapeNavigation);
